'use client';

import { useMemo } from 'react';
import { Icon, InputSelect, Tooltip } from '~/components/primitives';
import { useI18n } from '~/features/i18n';
import { cn } from '~/utils';
import { calculatePrice } from '@funkey/shared/activities';
import { Units } from '~/modules/units';
import { useVATRate, useShouldDisplayPrices } from '~/features/portals/money';
import type { ActivityPriceType } from '@funkey/constants';

export type VariantOption = {
    id: string;
    name?: string | null;
    duration?: string | null;
    extraCost?: number | null;
    extraCostPerCount?: number | null;
    priceRules?:
        ({ price: number; participants: number; extraPrice?: number | null } | null)[] | null;
};

export type LanguageOption = {
    id: string;
    name: string;
};

/* ---------------------------------------------------------------------------
 * useVariantOptions — builds variant dropdown options with price & duration
 * -------------------------------------------------------------------------*/

export function useVariantOptions(opts: {
    variants: readonly VariantOption[];
    participants: number | undefined;
    priceType: ActivityPriceType;
}) {
    const i18n = useI18n();
    const vat = useVATRate();
    const shouldDisplayPrices = useShouldDisplayPrices();

    const variantOptions = useMemo(() => {
        return opts.variants.map(v => {
            const parts: string[] = [v.name ?? ''];
            if (v.duration) parts.push(`${v.duration}h`);
            if (shouldDisplayPrices && v.priceRules?.length && opts.participants != null) {
                const prices = calculatePrice({
                    participants: opts.participants,
                    priceType: opts.priceType,
                    variant: v,
                });
                parts.push(
                    Units.formatEUR(vat.calcEUR(prices.pricePerParticipant) * 100, i18n.locale) +
                        '/pp',
                );
            }
            return { value: String(v.id), label: parts.join(' · ') };
        });
    }, [opts.variants, opts.participants, opts.priceType, vat, shouldDisplayPrices, i18n.locale]);

    return { variantOptions, shouldDisplayPrices };
}

/* ---------------------------------------------------------------------------
 * usePriceInfo — computes price for a selected variant
 * -------------------------------------------------------------------------*/

export function usePriceInfo(opts: {
    selectedVariant: VariantOption | undefined;
    participants: number | undefined;
    priceType: ActivityPriceType;
    shouldDisplayPrices: boolean;
}) {
    const vat = useVATRate();

    return useMemo(() => {
        if (!opts.selectedVariant || !opts.shouldDisplayPrices || opts.participants == null)
            return null;
        const prices = calculatePrice({
            participants: opts.participants,
            priceType: opts.priceType,
            variant: opts.selectedVariant,
        });
        return {
            pricePerParticipant: vat.calcEUR(prices.pricePerParticipant),
            total: vat.calcEUR(prices.price),
        };
    }, [opts.selectedVariant, opts.participants, opts.priceType, vat, opts.shouldDisplayPrices]);
}

/* ---------------------------------------------------------------------------
 * VariantLanguageInlineSelect — shared inline variant/language selector row
 * Used by both WishlistItemCard and CartItemInlineEdit.
 * -------------------------------------------------------------------------*/

type VariantLanguageInlineSelectProps = {
    id: string;
    isLoading?: boolean;
    disabled?: boolean;
    /** Variant */
    variants: readonly VariantOption[];
    singleVariant: VariantOption | null;
    singleVariantLabel?: string;
    singleVariantTooltip: string;
    variantValue?: string;
    variantOptions: { value: string; label: string }[];
    onVariantChange: (value: string | number) => void;
    /** Language */
    languages: readonly LanguageOption[];
    singleLanguage: LanguageOption | null;
    singleLanguageTooltip: string;
    languageValue?: string;
    onLanguageChange: (value: string | number) => void;
    /** Location select/label (optional) — rendered on its own line above the price. */
    locationSlot?: React.ReactNode;
    /** Price (optional) */
    priceSlot?: React.ReactNode;
    className?: string;
};

export const VariantLanguageInlineSelect: React.FC<VariantLanguageInlineSelectProps> = props => {
    const i18n = useI18n();

    const variantLabel =
        props.singleVariantLabel ??
        // Use the pre-built option label (includes price/pp) when available
        (props.variantOptions.length === 1
            ? props.variantOptions[0]!.label
            : (() => {
                  if (!props.singleVariant) return '';
                  const parts: string[] = [props.singleVariant.name ?? ''];
                  if (props.singleVariant.duration) parts.push(`${props.singleVariant.duration}h`);
                  return parts.join(' · ');
              })());

    return (
        <div
            className={cn(
                'flex w-full min-w-0 flex-col items-start gap-1.5 sm:flex-row sm:flex-wrap sm:items-center sm:gap-2',
                props.isLoading && 'animate-pulse',
                !props.singleVariant && '-ml-1 sm:-ml-2',
                props.className,
            )}
        >
            {props.singleVariant ? (
                <Tooltip content={props.singleVariantTooltip}>
                    <span className="block min-w-0 max-w-full truncate text-sm text-blue-600">
                        {variantLabel}
                    </span>
                </Tooltip>
            ) : (
                <InputSelect
                    id={`${props.id}-variant`}
                    compact
                    variant="minimal"
                    className="min-w-0 max-w-full"
                    disabled={props.disabled}
                    readonly={props.variants.length <= 1}
                    placeholder={i18n._('Select variant')}
                    value={props.variantValue}
                    options={props.variantOptions}
                    onChange={props.onVariantChange}
                />
            )}
            {props.singleLanguage ? (
                <Tooltip content={props.singleLanguageTooltip}>
                    <span className="block min-w-0 max-w-full truncate text-sm text-blue-600">
                        {props.singleLanguage.name}
                    </span>
                </Tooltip>
            ) : (
                <InputSelect
                    id={`${props.id}-language`}
                    compact
                    variant="minimal"
                    className="min-w-0 max-w-full"
                    disabled={props.disabled}
                    placeholder={i18n._('Select language')}
                    options={props.languages}
                    labelField="name"
                    valueField="id"
                    value={props.languageValue}
                    onChange={props.onLanguageChange}
                />
            )}
            {props.locationSlot ? (
                <div
                    className={cn(
                        'flex min-w-0 max-w-full basis-full items-center gap-1',
                        // Counter the container's negative margin only when it's applied.
                        !props.singleVariant && 'ml-2',
                    )}
                >
                    <Icon name="location" size="xs" className="shrink-0 text-blue-600" />
                    {props.locationSlot}
                </div>
            ) : null}
            {props.priceSlot ? (
                <div
                    className={cn('min-w-0 max-w-full basis-full', !props.singleVariant && 'ml-2')}
                >
                    {props.priceSlot}
                </div>
            ) : null}
        </div>
    );
};
